import {Component, EventEmitter, HostListener, Input, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TippsOverlayComponent} from "./overlay/tipps-overlay.component";
import {TranslateService} from "@ngx-translate/core";
import {EmbedService} from "../../services/embed.service";

/**
 * Component for showing tipps on the smarthec result page. Hast three layouts: list, smallist, single
 */
@Component({
    selector: 'app-tipps',
    templateUrl: './tipps.component.html',
    standalone: false
})
export class TippsComponent implements OnInit {

  /**
   * List of tipps to show
   */
  @Input() tipps: any[];

  /**
   * textkey for the headline
   */
  @Input() headline: string;

  /**
   * number of tipps to show on the start
   */
  @Input() tippCountDefault: number = null;

  /**
   * optional imagepath for showing images inside the list
   */
  @Input() imagePath: string = null;

  /**
   * current layout
   * One of list|smalllist|single
   */
  @Input() layout: string = 'list';

  /**
   * EventEmitter that can be given to trigger event on print start.
   */
  @Input() printStarter: EventEmitter<void> = null;

  /**
   * toggle for hiding the CTA
   */
  @Input() hideCta: boolean = false;

  /**
   * current number of tipps to show
   */
  tippCount: number;

  /**
   * basis for all tipps textkeys
   */
  textkeyBase: string = 'heizcheck.auswerten.tipps.';

  /**
   * object for showing the overlay
   */
  overlay: MatDialogRef<TippsOverlayComponent> = null;

  /**
   * name of the currently visible tipp.
   */
  currentTipp: string;


  scrollOffset: number = 0;

  /**
   * constructor
   */
  constructor(public dialog: MatDialog, private translate: TranslateService, private embedService: EmbedService) { }

  /**
   * initialize the component
   */
  ngOnInit(): void {
    // show all tipps at once on default
    if(this.tippCountDefault === null) {
      this.tippCountDefault = this.tipps.length;
    }

    // show default count on start
    this.tippCount = this.tippCountDefault;

    // check if one tipp should be open initially
    for(const tipp of this.tipps) {
      if(tipp.hasOwnProperty('open') && tipp.open === true) {
        this.showOverlay(tipp.name);
        break;
      }
    }

    if(this.embedService.isEmbedded()) {
      this.embedService.scrollEvent.subscribe(data => this.scrollOffset = data.offset);
    }

    if(this.printStarter) {
      this.printStarter.subscribe(() => this.onBeforePrint());
    }
  }

  /**
   * Opens the overlay with the currently clicked tipp
   */
  showOverlay(tipp: string): void {
    this.overlay = this.dialog.open(TippsOverlayComponent, {
      position: {
        top: (50 + (this.scrollOffset * -1)) + 'px'
      },
      data: {
        headline: this.headline,
        overall: this.tipps.length,
        autoFocus: false
      },
      panelClass: ['tipp-overlay', 'tipp-' + tipp]
    });

    // bind to event handler for showing prev/next tipp
    this.overlay.componentInstance.prev.subscribe(() => {
      let newIndex = (this.tipps.findIndex(element => element.name === this.currentTipp)) - 1;
      if(newIndex < 0) {
        newIndex = this.tipps.length - 1;
      }
      this.setTippData(this.tipps[newIndex].name);
    });
    this.overlay.componentInstance.next.subscribe(() => {
      let newIndex = (this.tipps.findIndex(element => element.name === this.currentTipp)) + 1;
      if(newIndex >= this.tipps.length) {
        newIndex = 0;
      }
      this.setTippData(this.tipps[newIndex].name);
    });

    this.setTippData(tipp);
  }

  /**
   * Sets all needed data/texts to the overlay.
   * @param tipp
   */
  setTippData(tipp: string) {
    if(this.overlay && this.overlay.componentInstance) {
      this.currentTipp = tipp;

      // get data of the tipp.
      const tippData = this.tipps.find(element => element.name === tipp);
      const descriptionTextPostfix = tippData.hasOwnProperty('descriptionTextPostfix')? '|' + tippData.descriptionTextPostfix : '';

      // all texts that are possible in the overlay with (name: textkey)
      const texts = {
        subheadline: this.textkeyBase + tipp,
        text: this.textkeyBase + tipp + '.text',
        description: this.textkeyBase + tipp + '.description' + descriptionTextPostfix,
        buttontext: this.hideCta? null : this.textkeyBase + tipp + '.buttontext',
        link: this.hideCta? null : this.textkeyBase + tipp + '.link',
        copyright: this.textkeyBase + tipp + '.copyright'
      }
      // get all text from the translator and set only the ones we got from the translator
      this.translate.get(Object.values(texts)).subscribe(data => {
        for(const text of Object.keys(texts)) {
          if(data.hasOwnProperty(texts[text]) && data[texts[text]] !== texts[text]) {
            this.overlay.componentInstance[text] = texts[text];
          }
          else {
            // reset the tipp so we have no mixed content.
            this.overlay.componentInstance[text] = null;
          }
        }
      });

      // set index
      this.overlay.componentInstance.index = (this.tipps.findIndex(element => element.name === tipp) + 1)

      // set placeholder
      if(tippData && tippData.hasOwnProperty('placeholder')) {
        this.overlay.componentInstance.placeholder = tippData.placeholder;
      }
      else {
        this.overlay.componentInstance.placeholder = null;
      }

      // set background image path
      this.overlay.componentInstance.image = this.imagePath + tipp + '_large.jpg';
    }
  }

  /**
   * Before printing the page, show all tipps
   */
  @HostListener('window:beforeprint')
  onBeforePrint(): void {
    this.tippCount = this.tipps.length;
  }

  /**
   * After printing the page, close all tipps back again.
   */
  @HostListener('window:afterprint')
  onAfterPrint(): void {
    this.tippCount = this.tippCountDefault;
  }

}
